import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ErrorApi, api } from '../api/cliente';
import type { Aliado, Archivo } from '../api/tipos';
import { useSesion } from '../auth/sesion';
import {
  AreaTexto,
  Aviso,
  Campo,
  Cargando,
  Casilla,
  Encabezado,
  Etiqueta,
  Seleccion,
  Tarjeta,
  Vacio,
} from '../ui/piezas';

const TIPOS = [
  { valor: 'PERSONA', texto: 'Comisionista' },
  { valor: 'INMOBILIARIA', texto: 'Inmobiliaria' },
  { valor: 'CONSTRUCTORA', texto: 'Constructora' },
];

interface Borrador {
  nombre: string;
  tipo: string;
  telefono: string;
  whatsapp: string;
  email: string;
  sitioWeb: string;
  notas: string;
  /**
   * El logo va y vuelve en cada guardado.
   *
   * <p>La API aplica el comando entero, y el formulario no mandaba `logoId`: el
   * primer «Guardar cambios» sobre un aliado con logo lo habría borrado. No
   * pasaba porque tampoco había dónde subirlo.
   */
  logoId: string | null;
  /** Lo que se enseña: la URL guardada, o la vista previa de lo recién elegido. */
  logoVista: string | null;
  activo: boolean;
}

const VACIO: Borrador = {
  nombre: '',
  tipo: 'PERSONA',
  telefono: '',
  whatsapp: '',
  email: '',
  sitioWeb: '',
  notas: '',
  logoId: null,
  logoVista: null,
  activo: true,
};

export default function Aliados() {
  const cache = useQueryClient();
  const { puede } = useSesion();
  const [forma, setForma] = useState<Borrador>(VACIO);
  const [editando, setEditando] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [exito, setExito] = useState<string | null>(null);
  const [subiendo, setSubiendo] = useState(false);
  const entradaLogo = useRef<HTMLInputElement>(null);

  /**
   * Sube el logo como cualquier otra imagen: se procesa en segundo plano y
   * sale en el portal con sus variantes. Aquí solo queda anotado el archivo.
   */
  const subirLogo = async (archivo: File | undefined) => {
    if (!archivo) return;
    setError(null);
    setSubiendo(true);
    try {
      const formulario = new FormData();
      formulario.append('archivo', archivo);
      const subido = await api<Archivo>('/admin/archivos?tipo=IMAGEN', {
        metodo: 'POST',
        formulario,
      });
      setForma((f) => ({ ...f, logoId: subido.id, logoVista: URL.createObjectURL(archivo) }));
    } catch (e) {
      setError(e);
    } finally {
      setSubiendo(false);
      if (entradaLogo.current) entradaLogo.current.value = '';
    }
  };

  const lista = useQuery({
    queryKey: ['aliados-admin'],
    queryFn: () => api<Aliado[]>('/admin/aliados?activos=false'),
  });

  const guardar = useMutation({
    mutationFn: () => {
      const cuerpo = {
        nombre: forma.nombre,
        tipo: forma.tipo,
        telefono: forma.telefono || null,
        whatsapp: forma.whatsapp || null,
        email: forma.email || null,
        sitioWeb: forma.sitioWeb || null,
        notas: forma.notas || null,
        logoId: forma.logoId,
        activo: forma.activo,
      };
      return editando
        ? api<Aliado>(`/admin/aliados/${editando}`, { metodo: 'PUT', cuerpo })
        : api<Aliado>('/admin/aliados', { metodo: 'POST', cuerpo });
    },
    onSuccess: () => {
      setError(null);
      setExito('Guardado.');
      setForma(VACIO);
      setEditando(null);
      void cache.invalidateQueries({ queryKey: ['aliados-admin'] });
      void cache.invalidateQueries({ queryKey: ['aliados'] });
    },
    onError: (e) => {
      setExito(null);
      setError(e);
    },
  });

  const desactivar = useMutation({
    mutationFn: (id: string) => api<Aliado>(`/admin/aliados/${id}`, { metodo: 'DELETE' }),
    onSuccess: () => {
      void cache.invalidateQueries({ queryKey: ['aliados-admin'] });
      void cache.invalidateQueries({ queryKey: ['aliados'] });
    },
    onError: setError,
  });

  const errorApi = error instanceof ErrorApi ? error : null;
  const editable = puede('configuracion:editar');

  return (
    <>
      <Encabezado
        titulo="Aliados"
        nota="Comisionistas y constructoras cuyo inventario se publica. No son usuarios: no entran al panel."
      />

      <Aviso error={error} exito={exito} />

      {editable && (
        <Tarjeta titulo={editando ? 'Editar aliado' : 'Registrar aliado'}>
          <div className="rejilla">
            <Campo
              etiqueta="Nombre"
              requerido
              valor={forma.nombre}
              alCambiar={(v) => setForma({ ...forma, nombre: v })}
              error={errorApi?.mensajeDe('nombre')}
            />
            <Seleccion
              etiqueta="Tipo"
              valor={forma.tipo}
              alCambiar={(v) => setForma({ ...forma, tipo: v })}
              opciones={TIPOS}
            />
            <Campo
              etiqueta="Teléfono"
              valor={forma.telefono}
              alCambiar={(v) => setForma({ ...forma, telefono: v })}
            />
            <Campo
              etiqueta="WhatsApp"
              valor={forma.whatsapp}
              alCambiar={(v) => setForma({ ...forma, whatsapp: v })}
            />
            <Campo
              etiqueta="Correo"
              tipo="email"
              valor={forma.email}
              alCambiar={(v) => setForma({ ...forma, email: v })}
              error={errorApi?.mensajeDe('email')}
            />
            <Campo
              etiqueta="Sitio web"
              valor={forma.sitioWeb}
              alCambiar={(v) => setForma({ ...forma, sitioWeb: v })}
            />
          </div>
          <p className="campo__ayuda">
            Hace falta al menos una forma de contacto: un aliado al que no se puede llamar no
            sirve para nada.
          </p>

          <div style={{ height: 12 }} />
          {/* El logo sale en la ficha del portal cuando el visitante contacta
              directo al aliado. Lo pedía el bosquejo: «logo, nombre y datos de
              contacto». */}
          <div className="campo">
            <label>Logo</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
              {forma.logoVista ? (
                <img
                  src={forma.logoVista}
                  alt=""
                  style={{
                    width: 56,
                    height: 56,
                    objectFit: 'contain',
                    padding: 4,
                    border: '1px solid var(--linea)',
                    borderRadius: 8,
                    background: '#fff',
                  }}
                />
              ) : (
                <span className="campo__ayuda" style={{ margin: 0 }}>
                  Sin logo
                </span>
              )}
              <input
                ref={entradaLogo}
                type="file"
                accept="image/png,image/jpeg,image/webp"
                style={{ display: 'none' }}
                onChange={(e) => void subirLogo(e.target.files?.[0])}
              />
              <button
                className="secundario"
                disabled={subiendo}
                onClick={() => entradaLogo.current?.click()}
              >
                {subiendo ? 'Subiendo…' : forma.logoId ? 'Cambiar logo' : 'Subir logo'}
              </button>
              {forma.logoId && (
                <button
                  className="plano"
                  onClick={() => setForma({ ...forma, logoId: null, logoVista: null })}
                >
                  Quitar
                </button>
              )}
            </div>
            <p className="campo__ayuda">
              PNG con fondo transparente, o sobre blanco. Se enseña pequeño, dentro de una caja
              blanca.
            </p>
          </div>

          <div style={{ height: 12 }} />
          <AreaTexto
            etiqueta="Notas internas"
            ayuda="Qué se pactó de comisión, quién lo presentó. Nunca sale al portal"
            valor={forma.notas}
            alCambiar={(v) => setForma({ ...forma, notas: v })}
            filas={2}
          />

          {/* Sin esto, un aliado desactivado no se podía volver a activar desde
              ninguna pantalla: solo había botón para desactivar. */}
          {editando && (
            <Casilla
              etiqueta="Activo"
              ayuda="Desactivado no se puede asignar a inmuebles nuevos; los que ya tiene siguen igual."
              valor={forma.activo}
              alCambiar={(v) => setForma({ ...forma, activo: v })}
            />
          )}

          <div className="fila fila--fin" style={{ marginTop: 14 }}>
            {editando && (
              <button
                className="secundario"
                onClick={() => {
                  setForma(VACIO);
                  setEditando(null);
                }}
              >
                Cancelar
              </button>
            )}
            <button disabled={guardar.isPending || !forma.nombre} onClick={() => guardar.mutate()}>
              {editando ? 'Guardar cambios' : 'Registrar'}
            </button>
          </div>
        </Tarjeta>
      )}

      <Tarjeta titulo="Registrados">
        {lista.isPending ? (
          <Cargando />
        ) : (lista.data ?? []).length === 0 ? (
          <Vacio>Todavía no hay aliados. El inventario es todo propio.</Vacio>
        ) : (
          <div className="tabla-marco">
            <table>
              <thead>
                <tr>
                  <th>Nombre</th>
                  <th>Tipo</th>
                  <th>Contacto</th>
                  <th>Estado</th>
                  {editable && <th />}
                </tr>
              </thead>
              <tbody>
                {(lista.data ?? []).map((a) => (
                  <tr key={a.id}>
                    <td>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                        {a.logo && (
                          <img
                            src={a.logo}
                            alt=""
                            style={{
                              width: 28,
                              height: 28,
                              objectFit: 'contain',
                              borderRadius: 4,
                              background: '#fff',
                            }}
                          />
                        )}
                        {a.nombre}
                      </span>
                    </td>
                    <td>{TIPOS.find((t) => t.valor === a.tipo)?.texto}</td>
                    <td>
                      {[a.telefono, a.whatsapp, a.email].filter(Boolean).join(' · ') || '—'}
                    </td>
                    <td>
                      <Etiqueta>{a.activo ? 'ACTIVO' : 'ARCHIVADO'}</Etiqueta>
                    </td>
                    {editable && (
                      <td>
                        <button
                          className="plano"
                          onClick={() => {
                            setEditando(a.id);
                            setForma({
                              nombre: a.nombre,
                              tipo: a.tipo,
                              telefono: a.telefono ?? '',
                              whatsapp: a.whatsapp ?? '',
                              email: a.email ?? '',
                              sitioWeb: a.sitioWeb ?? '',
                              notas: a.notas ?? '',
                              logoId: a.logoId,
                              logoVista: a.logo,
                              activo: a.activo,
                            });
                            window.scrollTo({ top: 0, behavior: 'smooth' });
                          }}
                        >
                          Editar
                        </button>
                        {a.activo && (
                          <button className="plano" onClick={() => desactivar.mutate(a.id)}>
                            Desactivar
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Tarjeta>
    </>
  );
}
